---
id: dockerfile-optimization
title: Dockerfile Optimization
category: containers
difficulty: beginner
tags:
  - docker
  - dockerfile
  - optimization
  - security
  - image-size
  - multi-stage
description: >
  Reviews and optimizes a Dockerfile for size, security, build speed,
  and best practices.
whenToUse: >
  When Docker images are too large, builds are slow, or you need to
  harden container images.
variables:
  - name: dockerfile
    desc: The Dockerfile content to review
  - name: app_type
    desc: Application type (Node.js, Python, Go, Java, etc.)
  - name: base_preference
    desc: Preferred base image style
  - name: size_goal
    desc: Target image size (e.g., "under 100MB")
tips:
  - Always use multi-stage builds for compiled languages — the builder stage doesn't ship to production.
  - Order COPY commands from least to most frequently changing for optimal caching.
  - Use .dockerignore to exclude node_modules, .git, and test files from the build context.
---

You are a container security and optimization expert. Review this Dockerfile.

## Dockerfile
```dockerfile
{{dockerfile}}
```

## Context
- Application type: {{app_type}}
- Base image preference: {{base_preference}} (alpine, distroless, debian-slim)
- Target image size goal: {{size_goal}}

## Review Categories
1. **Size** — Layer optimization, multi-stage builds, .dockerignore
2. **Security** — Non-root user, minimal base image, no secrets in layers, pinned versions
3. **Build Speed** — Cache-friendly layer ordering, minimal COPY invalidation
4. **Reproducibility** — Pinned versions, deterministic builds

## Output Format (JSON)
{
  "current_issues": [
    {
      "line": "Dockerfile line number or content",
      "category": "size | security | speed | reproducibility",
      "severity": "critical | high | medium | low",
      "issue": "description",
      "fix": "how to fix"
    }
  ],
  "optimized_dockerfile": "complete optimized Dockerfile",
  "estimated_improvements": {
    "size_reduction": "percentage or MB estimate",
    "build_speed": "improvement estimate",
    "security_score": "before → after"
  }
}

<!-- EXAMPLE_OUTPUT -->

{
  "current_issues": [
    {
      "line": "FROM node:18",
      "category": "size",
      "severity": "high",
      "issue": "Using full node image (1.1GB) instead of slim/alpine variant",
      "fix": "Use node:18-slim (200MB) or node:18-alpine (180MB)"
    },
    {
      "line": "COPY . .",
      "category": "speed",
      "severity": "medium",
      "issue": "Copying everything before npm install invalidates dependency cache on any code change",
      "fix": "COPY package*.json first, run npm install, then COPY the rest"
    }
  ],
  "optimized_dockerfile": "FROM node:18-slim AS builder\n..."
}
